import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { Menu, X, Instagram } from "lucide-react";

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const { content } = useContent();
  const contact = content?.contact || {};
  const igLink = contact.instagram || "https://www.instagram.com/our_promise_events";
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/ueber-uns", label: t("nav.about") },
    { to: "/packages", label: t("nav.packages") },
    { to: "/services", label: t("nav.services") },
    { to: "/eindruecke", label: t("nav.impressions") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/kontakt", label: t("nav.contact") },
  ];

  const solid = scrolled || open || pathname !== "/";
  const textColor = solid ? "text-ink" : "text-cream";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        solid ? "bg-cream/95 backdrop-blur border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center shrink-0">
          <img src="/fotos/logo.png?v=3" alt="Our Promise Events" className="h-10 md:h-11 lg:h-14 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-3 lg:gap-5 xl:gap-6">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `elegant-link text-[10px] lg:text-[11px] xl:text-xs tracking-[0.15em] xl:tracking-[0.18em] uppercase whitespace-nowrap transition-colors text-gold ${
                  isActive ? "opacity-100" : "opacity-80 hover:opacity-100"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href={igLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram"
            aria-label="Instagram"
            title="@our_promise_events"
            className={`hidden sm:inline-flex items-center justify-center w-8 h-8 rounded-full border transition-all duration-300 ${
              solid ? "border-gold/60 text-gold hover:bg-gold hover:text-cream" : "border-cream/60 text-cream hover:bg-cream hover:text-ink"
            }`}
          >
            <Instagram size={15} strokeWidth={1.5} />
          </a>

          <div className={`hidden sm:flex items-center gap-1 text-xs tracking-widest uppercase ${textColor}`} data-testid="language-switcher">
            <button data-testid="lang-de" onClick={() => setLang("de")} className={`px-1.5 ${lang === "de" ? "opacity-100 underline" : "opacity-60 hover:opacity-100"}`}>DE</button>
            <span className="opacity-50">|</span>
            <button data-testid="lang-fr" onClick={() => setLang("fr")} className={`px-1.5 ${lang === "fr" ? "opacity-100 underline" : "opacity-60 hover:opacity-100"}`}>FR</button>
          </div>

          <Link
            to="/termin"
            data-testid="nav-cta-consultation"
            className="hidden xl:inline-block bg-ink text-cream px-6 py-3 text-[11px] tracking-[0.2em] uppercase hover:bg-gold transition-colors duration-500"
          >
            {t("nav.cta")}
          </Link>

          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" className={`md:hidden p-1.5 ${textColor}`} aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-cream border-t border-border" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`} className="text-sm tracking-[0.2em] uppercase text-gold">
                {l.label}
              </NavLink>
            ))}
            <Link to="/termin" data-testid="mobile-nav-cta" className="bg-ink text-cream px-6 py-3 text-center text-[11px] tracking-[0.2em] uppercase mt-2">
              {t("nav.cta")}
            </Link>
            <a
              href={igLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-instagram"
              className="flex items-center gap-3 text-sm tracking-[0.2em] uppercase text-gold"
            >
              <Instagram size={16} strokeWidth={1.5} /> @our_promise_events
            </a>
            <div className="flex gap-3 pt-4 border-t border-border text-xs tracking-widest uppercase text-ink">
              <button data-testid="mobile-lang-de" onClick={() => setLang("de")} className={lang === "de" ? "underline" : "opacity-60"}>Deutsch</button>
              <span>|</span>
              <button data-testid="mobile-lang-fr" onClick={() => setLang("fr")} className={lang === "fr" ? "underline" : "opacity-60"}>Français</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
